import path from 'path';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, ListGroup, ListGroupItem, FormGroup, Input, Button } from 'reactstrap';
import Icon from '../common/containers/icon';
import appHistory from '../../utils/appHistory';
import { getError } from '../../actions/ajax';
import entityComposition from '../common/classes/entityComposition';
import { FAVORITES_UNAVAILABLE_USER_MESSAGE, FavoritesUnavailableError } from './favoritesHelpers';
import { USER_PREFERENCES_TYPE, useUserPreferencesFavorites } from './userPreferencesFavorites';
const renderFavoriteListItem = ({
  favorite,
  isActive,
  onClick,
  onMouseEnter,
  onDelete
}) => {
  const showDelete = favorite.type === 'pinned' && !favorite.isSavedView;
  return /*#__PURE__*/React.createElement(ListGroupItem, {
    key: favorite.id,
    className: `d-flex align-items-center p-0 ${isActive ? 'active' : ''}`,
    onMouseEnter: onMouseEnter
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "list-group-item-action flex-grow-1 border-0 bg-transparent text-left d-flex align-items-center w-100 py-3 pl-3",
    style: {
      color: 'inherit'
    },
    onClick: onClick
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: favorite.icon,
    className: isActive ? 'text-white' : 'text-muted',
    style: {
      marginRight: '10px',
      width: '16px',
      textAlign: 'center',
      flexShrink: 0
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex-grow-1 mr-2",
    style: {
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "font-weight-bold text-truncate"
  }, favorite.title), favorite.subtitle && /*#__PURE__*/React.createElement("div", {
    className: `small text-truncate ${isActive ? 'text-white-50' : 'text-muted'}`
  }, favorite.subtitle))), showDelete && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: `btn p-0 border-0 bg-transparent flex-shrink-0 d-flex align-items-center justify-content-center py-3 pr-3 pl-2 ${isActive ? 'text-white' : 'text-muted'}`,
    onClick: onDelete,
    "aria-label": `Delete ${favorite.title}`,
    title: `Delete ${favorite.title}`
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "times",
    style: {
      fontSize: '1.125rem',
      width: '16px',
      textAlign: 'center'
    }
  })));
};
const FavoritesModal = ({
  isOpen,
  onClose,
  query,
  onQueryChange,
  favorites,
  notificationActions: _notificationActions = {}
}) => {
  const cabinetSegment = useSelector(state => {
    var _state$cabinet;
    return (_state$cabinet = state.cabinet) === null || _state$cabinet === void 0 ? void 0 : _state$cabinet.currentCabinet;
  });
  const {
    removePinnedFavorite,
    ensureLoaded,
    loading: preferencesLoading
  } = useUserPreferencesFavorites();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus search when modal opens
  useEffect(() => {
    if (isOpen) {
      var _searchInputRef$curre;
      (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
    }
  }, [isOpen]);
  const markFavoriteAsRemoved = useCallback(async pinId => {
    try {
      const removed = await removePinnedFavorite(pinId);
      return removed;
    } catch (err) {
      if (err instanceof FavoritesUnavailableError) {
        if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
          _notificationActions.error({
            headerMessage: 'Failed to remove favorite',
            message: FAVORITES_UNAVAILABLE_USER_MESSAGE,
            id: 'favorite-remove-unavailable'
          });
        }
        return false;
      }
      const {
        error_msg: errorMsg
      } = getError(err);
      if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
        _notificationActions.error({
          headerMessage: 'Failed to remove favorite',
          message: errorMsg || 'Please try again.',
          id: 'favorite-remove-failed'
        });
      }
      return false;
    }
  }, [removePinnedFavorite, _notificationActions]);
  const handleItemClick = useCallback(index => {
    if (favorites[index]) {
      favorites[index].onSelect();
      onClose();
    }
  }, [favorites, onClose]);
  const handleItemMouseEnter = useCallback(index => {
    setSelectedIndex(index);
  }, []);
  const handleDeleteFavorite = useCallback(async favoriteIndex => {
    const favoriteToDelete = favorites[favoriteIndex];
    if ((favoriteToDelete === null || favoriteToDelete === void 0 ? void 0 : favoriteToDelete.type) === 'pinned' && !favoriteToDelete.isSavedView) {
      await markFavoriteAsRemoved(favoriteToDelete.pinId);
    }
  }, [favorites, markFavoriteAsRemoved]);
  const handleViewPreferences = useCallback(async () => {
    try {
      const formId = await ensureLoaded();
      if (!formId) {
        if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
          _notificationActions.error({
            headerMessage: 'Could not open preferences',
            message: FAVORITES_UNAVAILABLE_USER_MESSAGE,
            id: 'favorites-view-preferences-unavailable'
          });
        }
        return;
      }
      const url = cabinetSegment ? path.join('/cabinets', cabinetSegment, USER_PREFERENCES_TYPE, formId) : path.join('/forms', USER_PREFERENCES_TYPE, formId);
      appHistory.push(url);
      onClose();
    } catch (err) {
      const {
        error_msg: errorMsg
      } = getError(err);
      if (_notificationActions !== null && _notificationActions !== void 0 && _notificationActions.error) {
        _notificationActions.error({
          headerMessage: 'Could not open preferences',
          message: errorMsg || 'Please try again.',
          id: 'favorites-view-preferences-failed'
        });
      }
    }
  }, [ensureLoaded, cabinetSegment, onClose, _notificationActions]);
  if (!isOpen) return null;
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    toggle: onClose,
    backdrop: true,
    modalClassName: "wide-modal t-scrollable-modal"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: onClose
  }, "Search Favorites"), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(FormGroup, {
    className: "mb-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group"
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group-prepend"
  }, /*#__PURE__*/React.createElement("span", {
    className: "input-group-text"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "search"
  }))), /*#__PURE__*/React.createElement(Input, {
    innerRef: searchInputRef,
    type: "text",
    bsSize: "md",
    placeholder: "Type to search favorites...",
    value: query,
    onChange: e => onQueryChange(e.target.value),
    className: "tqc-control"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "t-scrollable-modal__scroll mt-2"
  }, favorites.length > 0 ? /*#__PURE__*/React.createElement(ListGroup, {
    flush: true
  }, favorites.map((favorite, index) => renderFavoriteListItem({
    favorite,
    isActive: index === selectedIndex,
    onClick: () => handleItemClick(index),
    onMouseEnter: () => handleItemMouseEnter(index),
    onDelete: () => handleDeleteFavorite(index)
  }))) : /*#__PURE__*/React.createElement("div", {
    className: "p-3 text-center text-muted"
  }, "No favorites found"))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    color: "secondary",
    onClick: onClose
  }, "Close"), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    color: "primary",
    onClick: handleViewPreferences,
    disabled: preferencesLoading
  }, "View preferences")));
};
FavoritesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  favorites: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    icon: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    pinId: PropTypes.string,
    isSavedView: PropTypes.bool
  })).isRequired,
  notificationActions: PropTypes.shape({
    info: PropTypes.func,
    error: PropTypes.func
  })
};
export { FavoritesModal as UncomposedFavoritesModal };
export default entityComposition(FavoritesModal, [], {}, ['notification']);